import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { CurrentMember } from '../../shared/models';
import type { PublicWorkspaceInvitation } from '../../shared/workspace-membership';
import { api } from '../api';
import { useI18n } from '../i18n';
import { localizedError } from '../i18n/errors';
import { permissionPresentation, roleName } from '../presentation/kitchen-language';
import { useUnsavedChanges } from '../unsaved-changes';

export function InviteAcceptPage({ token, member, onLogin, onAccepted, onChangeAccount }: {
  token: string;
  member: CurrentMember | null;
  onLogin: (email: string, password: string) => Promise<void>;
  onAccepted: (workspaceId: string, member: CurrentMember | null) => Promise<void>;
  onChangeAccount: () => Promise<void>;
}) {
  const { locale } = useI18n();
  const [invitation, setInvitation] = useState<PublicWorkspaceInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  const [error, setError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationProof, setVerificationProof] = useState('');
  const [verificationBusy, setVerificationBusy] = useState<'send' | 'verify' | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationNotice, setVerificationNotice] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationExpiresAt, setVerificationExpiresAt] = useState('');
  const [resendAfter, setResendAfter] = useState('');
  const [resendReady, setResendReady] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api.invitationByToken(token).then(result => {
      if (!cancelled) setInvitation(result.invitation);
    }).catch(err => {
      if (!cancelled) setError(localizedError(err, locale, {
        'zh-CN': '邀请无法读取，可能已失效。',
        en: 'This invitation could not be loaded and may no longer be valid.'
      }));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, locale]);

  useEffect(() => {
    if (!resendAfter) { setResendReady(true); return; }
    const wait = new Date(resendAfter).getTime() - Date.now();
    if (wait <= 0) { setResendReady(true); return; }
    setResendReady(false);
    const timer = window.setTimeout(() => setResendReady(true), wait + 100);
    return () => window.clearTimeout(timer);
  }, [resendAfter]);

  const targetMatches = Boolean(member && invitation && member.email.toLowerCase() === invitation.email.toLowerCase());
  const permissionPreview = useMemo(() => (invitation?.permissions ?? []).slice(0, 8), [invitation]);
  const newAccountFormDirty = Boolean(invitation && !invitation.accountExists && !member
    && (displayName || password || confirmPassword || verificationCode || verificationProof));
  useUnsavedChanges(newAccountFormDirty);

  async function login(event: FormEvent) {
    event.preventDefault();
    if (!invitation || loginBusy) return;
    setLoginBusy(true); setLoginError('');
    try {
      await onLogin(invitation.email, password);
      setPassword('');
    } catch (err) {
      setLoginError(localizedError(err, locale, {
        'zh-CN': '登录失败，请确认受邀邮箱和密码。',
        en: 'Sign-in failed. Check the invited email and password.'
      }));
    } finally { setLoginBusy(false); }
  }

  async function sendVerification() {
    if (!invitation || invitation.accountExists || member || verificationBusy || !resendReady) return;
    setVerificationBusy('send'); setVerificationError(''); setVerificationNotice(''); setError('');
    try {
      const result = await api.requestInvitationEmailVerification(token);
      setVerificationSent(true); setVerificationCode(''); setVerificationProof('');
      setVerificationExpiresAt(result.expiresAt); setResendAfter(result.resendAfter);
      setVerificationNotice(locale === 'zh-CN'
        ? `验证码已发送到 ${invitation.email}。请输入邮件中的 6 位数字。`
        : `A verification code was sent to ${invitation.email}. Enter the 6-digit code from the email.`);
    } catch (err) {
      setVerificationError(localizedError(err, locale, {
        'zh-CN': '验证码发送失败，请稍后重试。',
        en: 'Failed to send the verification code. Please try again later.'
      }));
    } finally { setVerificationBusy(null); }
  }

  async function verifyEmail() {
    if (!invitation || verificationBusy || !/^\d{6}$/.test(verificationCode.trim())) return;
    setVerificationBusy('verify'); setVerificationError(''); setVerificationNotice(''); setError('');
    try {
      const result = await api.verifyInvitationEmail(token, verificationCode.trim());
      setVerificationProof(result.verificationProof); setVerificationCode('');
      setVerificationExpiresAt(result.expiresAt);
      setVerificationNotice(locale === 'zh-CN'
        ? '邮箱已验证，现在可以创建账号并接受邀请。'
        : 'The invited mailbox is verified. You can now safely create the Account and accept the invitation.');
    } catch (err) {
      setVerificationError(localizedError(err, locale, {
        'zh-CN': '邮箱验证失败，请检查验证码或重新发送。',
        en: 'Email verification failed. Check the code or send a new one.'
      }));
    } finally { setVerificationBusy(null); }
  }

  async function accept() {
    if (!invitation || busy || invitation.status !== 'pending') return;
    if (!invitation.accountExists) {
      if (!verificationProof) { setError(locale === 'zh-CN' ? '请先验证受邀邮箱。' : 'Verify the invited mailbox first.'); return; }
      if (!displayName.trim()) { setError(locale === 'zh-CN' ? '请填写你的名称。' : 'Enter your display name.'); return; }
      if (password.length < 12) { setError(locale === 'zh-CN' ? '密码至少 12 位。' : 'Password must be at least 12 characters.'); return; }
      if (password !== confirmPassword) { setError(locale === 'zh-CN' ? '两次输入的密码不一致。' : 'The passwords do not match.'); return; }
    }
    setBusy(true); setError('');
    try {
      const result = await api.acceptInvitation(token, invitation.accountExists ? {} : {
        displayName: displayName.trim(), password, emailVerificationProof: verificationProof
      });
      setVerificationProof(''); setPassword(''); setConfirmPassword('');
      await onAccepted(result.accepted.workspaceId, result.member);
    } catch (err) {
      setError(localizedError(err, locale, {
        'zh-CN': '接受邀请失败，请刷新邀请状态后重试。',
        en: 'Could not accept the invitation. Refresh its status and try again.'
      }));
    } finally { setBusy(false); }
  }

  if (loading) return <div className="invite-screen"><div className="invite-card"><p className="invite-loading">{locale === 'zh-CN' ? '正在读取邀请…' : 'Loading invitation…'}</p></div></div>;
  if (!invitation) return <div className="invite-screen"><div className="invite-card"><span className="eyebrow">{locale === 'zh-CN' ? '邀请' : 'Invitation'}</span><h1>{locale === 'zh-CN' ? '邀请不可用' : 'Invitation unavailable'}</h1>{error && <p className="form-error">{error}</p>}</div></div>;

  const closed = invitation.status !== 'pending';
  const expires = new Date(invitation.expiresAt).toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en-US');
  const verificationExpiry = verificationExpiresAt ? new Date(verificationExpiresAt).toLocaleTimeString(locale === 'zh-CN' ? 'zh-CN' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '';
  const statusLabel = invitation.status === 'accepted'
    ? (locale === 'zh-CN' ? '已接受' : 'Accepted')
    : invitation.status === 'expired'
      ? (locale === 'zh-CN' ? '已过期' : 'Expired')
      : invitation.status === 'revoked'
        ? (locale === 'zh-CN' ? '已撤销' : 'Revoked')
        : (locale === 'zh-CN' ? '待接受' : 'Pending');

  const statusCopy = invitation.status === 'accepted'
    ? (locale === 'zh-CN' ? '这个邀请已经被接受。' : 'This invitation has already been accepted.')
    : invitation.status === 'expired'
      ? (locale === 'zh-CN' ? '这个邀请已经过期，请联系管理员重新邀请。' : 'This invitation has expired. Ask the restaurant for a new invitation.')
      : invitation.status === 'revoked'
        ? (locale === 'zh-CN' ? '这个邀请已经被撤销。' : 'This invitation has been revoked.')
        : '';

  return <div className="invite-screen">
    <main className="invite-card">
      <header className="invite-head">
        <div><span className="eyebrow">{locale === 'zh-CN' ? '邀请' : 'Invitation'}</span><h1>{locale === 'zh-CN' ? '加入空间' : 'Join workspace'}</h1></div>
        <span className={`invite-status ${closed ? 'is-closed' : ''}`}>{statusLabel}</span>
      </header>

      <section className="invite-workspace-summary">
        <span>{locale === 'zh-CN' ? '受邀加入' : 'Invited to'}</span>
        <strong>{invitation.workspaceName}</strong>
        <p>{invitation.email}</p>
      </section>

      <div className="invite-meta-grid">
        <div><span>{locale === 'zh-CN' ? '角色' : 'Roles'}</span><div className="role-tags">{invitation.roles.map(role => <span key={role.key}>{roleName(role.key, locale)}</span>)}</div></div>
        <div><span>{locale === 'zh-CN' ? '有效期' : 'Expires'}</span><strong>{expires}</strong></div>
        <div><span>{locale === 'zh-CN' ? '邀请人' : 'Invited by'}</span><strong>{invitation.inviterDisplayName ?? '—'}</strong></div>
      </div>

      <section className="invite-permissions">
        <div><strong>{locale === 'zh-CN' ? `将获得 ${invitation.permissions.length} 项权限` : `${invitation.permissions.length} permissions included`}</strong><p>{locale === 'zh-CN' ? '主管理员权限不能通过普通邀请获得。' : 'Owner is never granted by invitation; ownership uses a separate protected workflow.'}</p></div>
        <div className="invite-permission-list">{permissionPreview.map(permission => <span key={permission}>{permissionPresentation(permission, locale).title}</span>)}</div>
      </section>

      {closed ? <p className="locked-note">{statusCopy}</p> : <>
        {error && <p className="form-error">{error}</p>}

        {invitation.accountExists && !member && <form className="invite-action-panel" onSubmit={login}>
          <div><strong>{locale === 'zh-CN' ? '这个邮箱已有账号' : 'This email already has a Menu Lab account'}</strong><p>{locale === 'zh-CN' ? '输入这个账号的密码后继续。' : 'Verify the existing account password before accepting. The invitation never bypasses sign-in.'}</p></div>
          <label>{locale === 'zh-CN' ? '受邀邮箱' : 'Invited email'}<input value={invitation.email} disabled /></label>
          <label>{locale === 'zh-CN' ? '密码' : 'Password'}<input type="password" autoComplete="current-password" value={password} disabled={loginBusy} onChange={event => setPassword(event.target.value)} required /></label>
          {loginError && <p className="form-error">{loginError}</p>}
          <button className="primary-button" disabled={loginBusy || !password}>{loginBusy ? (locale === 'zh-CN' ? '登录中…' : 'Signing in…') : (locale === 'zh-CN' ? '登录并继续' : 'Sign in and continue')}</button>
        </form>}

        {member && !targetMatches && <div className="invite-action-panel danger-panel">
          <div><strong>{locale === 'zh-CN' ? '当前登录账号与受邀邮箱不一致' : 'The signed-in account does not match the invitation'}</strong><p>{locale === 'zh-CN' ? `当前：${member.email}；受邀：${invitation.email}。请切换账号后继续。` : `Signed in: ${member.email}; invited: ${invitation.email}. Switch accounts to continue.`}</p></div>
          <button className="text-button danger-text" type="button" disabled={busy} onClick={() => { onChangeAccount().catch(console.error); }}>{locale === 'zh-CN' ? '退出并切换账号' : 'Sign out and switch account'}</button>
        </div>}

        {!invitation.accountExists && !member && <>
          <section className="invite-action-panel invite-verification-panel">
            <div><strong>{locale === 'zh-CN' ? '先验证受邀邮箱' : 'Verify the invited mailbox first'}</strong><p>{locale === 'zh-CN' ? '为了确认这是你的邮箱，需要先输入邮件验证码。' : 'The invitation link proves access to the link, not ownership of this mailbox. A new Account requires a second email verification step.'}</p></div>
            <label>{locale === 'zh-CN' ? '受邀邮箱' : 'Invited email'}<input value={invitation.email} disabled /></label>
            {!verificationProof && <div className="invite-verification-actions">
              <button className="secondary-button" type="button" disabled={Boolean(verificationBusy) || !resendReady} onClick={() => { sendVerification().catch(console.error); }}>{verificationBusy === 'send' ? (locale === 'zh-CN' ? '发送中…' : 'Sending…') : verificationSent ? (locale === 'zh-CN' ? '重新发送验证码' : 'Send another code') : (locale === 'zh-CN' ? '发送邮箱验证码' : 'Send verification code')}</button>
              {verificationSent && <label>{locale === 'zh-CN' ? '6 位验证码' : '6-digit code'}<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" value={verificationCode} disabled={Boolean(verificationBusy)} onChange={event => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))} /><span className="field-help">{verificationExpiry ? (locale === 'zh-CN' ? `本次验证码约在 ${verificationExpiry} 失效。` : `This code expires around ${verificationExpiry}.`) : ''}</span></label>}
              {verificationSent && <button className="primary-button compact" type="button" disabled={Boolean(verificationBusy) || verificationCode.length !== 6} onClick={() => { verifyEmail().catch(console.error); }}>{verificationBusy === 'verify' ? (locale === 'zh-CN' ? '验证中…' : 'Verifying…') : (locale === 'zh-CN' ? '验证邮箱' : 'Verify email')}</button>}
            </div>}
            {verificationProof && <p className="form-success">{locale === 'zh-CN' ? '邮箱已验证。本次验证只用于当前邀请，接受后会立即失效。' : 'Mailbox verified. This proof is valid only for this invitation and is consumed when you accept it.'}</p>}
            {verificationNotice && !verificationProof && <p className="form-success">{verificationNotice}</p>}
            {verificationError && <p className="form-error">{verificationError}</p>}
          </section>

          <div className={`invite-action-panel${verificationProof ? '' : ' is-locked'}`}>
            <div><strong>{locale === 'zh-CN' ? '创建账号' : 'Create an account through this invitation'}</strong><p>{locale === 'zh-CN' ? '邮箱验证通过后会创建账号，并加入这个空间。' : 'The Account is created only after mailbox verification; then a membership is added only in this Workspace and you are signed in.'}</p></div>
            <label>{locale === 'zh-CN' ? '名称' : 'Display name'}<input maxLength={120} value={displayName} disabled={busy || !verificationProof} onChange={event => setDisplayName(event.target.value)} required /></label>
            <label>{locale === 'zh-CN' ? '密码' : 'Password'}<input type="password" minLength={12} maxLength={200} autoComplete="new-password" value={password} disabled={busy || !verificationProof} onChange={event => setPassword(event.target.value)} required /></label>
            <label>{locale === 'zh-CN' ? '确认密码' : 'Confirm password'}<input type="password" minLength={12} maxLength={200} autoComplete="new-password" value={confirmPassword} disabled={busy || !verificationProof} onChange={event => setConfirmPassword(event.target.value)} required /></label>
          </div>
        </>}

        {(targetMatches || (!invitation.accountExists && !member)) && <div className="invite-accept-row">
          {targetMatches && <p>{locale === 'zh-CN' ? `已验证账号：${member?.email}` : `Verified account: ${member?.email}`}</p>}
          {!invitation.accountExists && !member && !verificationProof && <p>{locale === 'zh-CN' ? '先完成邮箱验证，才能创建账号。' : 'Complete mailbox verification before creating the Account.'}</p>}
          <button className="primary-button" type="button" disabled={busy || (!targetMatches && (invitation.accountExists || !verificationProof))} onClick={() => { accept().catch(console.error); }}>{busy ? (locale === 'zh-CN' ? '加入中…' : 'Joining…') : (locale === 'zh-CN' ? `接受并加入 ${invitation.workspaceName}` : `Accept and join ${invitation.workspaceName}`)}</button>
        </div>}
      </>}
    </main>
  </div>;
}
